import React, { useState } from 'react';

const metricThresholds = {
  performanceScore: { type: 'higher', good: 90, needsImprovement: 50 },
  lcp: { type: 'lower', good: 2.5, needsImprovement: 4.0 },
  fcp: { type: 'lower', good: 1.8, needsImprovement: 3.0 },
  cls: { type: 'lower', good: 0.1, needsImprovement: 0.25 },
  speedIndex: { type: 'lower', good: 3.4, needsImprovement: 5.8 },
  tbt: { type: 'lower', good: 200, needsImprovement: 600 } // values in ms
};

const normalizeMetricValue = (metricKey, metric) => {
  if (!metric || metric.value === null || metric.value === undefined) {
    if (metric?.displayValue?.includes('s')) {
      const parsed = parseFloat(metric.displayValue);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return metric?.value ?? null;
  }
  return metric.value;
};

const deriveStatus = (metricKey, metric) => {
  if (!metric) return 'N/A';
  const thresholds = metricThresholds[metricKey];
  const value = normalizeMetricValue(metricKey, metric);
  if (!thresholds || value === null || value === undefined) {
    return 'N/A';
  }
  if (thresholds.type === 'higher') {
    if (value >= thresholds.good) return 'Good';
    if (value >= thresholds.needsImprovement) return 'Needs Improvement';
    return 'Poor';
  }

  // lower is better
  if (value <= thresholds.good) return 'Good';
  if (value <= thresholds.needsImprovement) return 'Needs Improvement';
  return 'Poor';
};

const renderWebVital = (metricKey, vitalData) => {
  const status = deriveStatus(metricKey, vitalData);
  const statusClass = status?.toLowerCase().replace(' ', '-') || 'unknown';

  return (
    <div className={`web-vital-metric ${statusClass}`}>
    <p className="metric-value">{vitalData?.displayValue || 'N/A'}</p>
      <span className={`metric-status ${statusClass}`}>
        {status || 'N/A'}
    </span>
  </div>
);
};

const PerformanceDetails = ({ performanceData, loading, error, onRescan }) => {
  const [isRescanning, setIsRescanning] = useState(false);

  const handleRescan = async () => {
    console.log('Rescan button clicked');
    setIsRescanning(true);
    try {
      await onRescan(true);
      console.log('onRescan completed');
    } catch (error) {
      console.error('Error during rescan:', error);
    } finally {
      setIsRescanning(false);
      console.log('Rescanning finished');
    }
  };

  if (error) {
    return (
      <div className="tab-content performance-details">
        <div className="performance-error" role="alert">{error}</div>
      </div>
    );
  }

  if (loading && !performanceData) {
    return (
      <div className="tab-content performance-details">
        <div className="performance-loading">
          <span className="performance-spinner" role="status" aria-hidden="true" />
          <span>Loading performance data...</span>
        </div>
      </div>
    );
  }

  if (!performanceData) {
    return null;
  }

  const mobileMetrics = performanceData.mobile || {};
  const desktopMetrics = performanceData.desktop || {};
  
  // Check if performance scanning is disabled or has an error message
  const isDisabled = performanceData.disabled === true;
  const errorMessage = performanceData.message || performanceData.rawError;

  return (
    <div className="tab-content performance-details">
      {isDisabled && errorMessage && (
        <div className="performance-alert performance-alert-warning" role="alert">
          <strong>⚠️ Performance Scanning Unavailable:</strong> {errorMessage}
          {errorMessage.includes('GOOGLE_API_KEY') && (
            <div className="performance-alert-detail">
              Please configure the GOOGLE_API_KEY environment variable in your backend server to enable performance scanning.
            </div>
          )}
        </div>
      )}
      <div className="performance-header">
        <div className="performance-actions">
          <button
            type="button"
            className="performance-rescan-btn"
            onClick={handleRescan}
            disabled={isRescanning}
          >
            {isRescanning ? (
              <>
                <span className="performance-spinner" role="status" aria-hidden="true" />
                Scanning...
              </>
            ) : (
              'Rescan Performance'
            )}
          </button>
        </div>
      </div>
      <div className="performance-grid">
        <div className="performance-card">
          <h3 className="performance-card-title">Core Web Vitals - Mobile</h3>
          <div className="web-vitals-container">
            <div className="web-vital-group">
              <h4 className="web-vital-label">Performance Score</h4>
              {renderWebVital('performanceScore', mobileMetrics.performanceScore)}
            </div>
            <div className="web-vital-group">
              <h4 className="web-vital-label">LCP</h4>
              {renderWebVital('lcp', mobileMetrics.lcp)}
            </div>
            <div className="web-vital-group">
              <h4 className="web-vital-label">CLS</h4>
              {renderWebVital('cls', mobileMetrics.cls)}
            </div>
            <div className="web-vital-group">
              <h4 className="web-vital-label">FCP</h4>
              {renderWebVital('fcp', mobileMetrics.fcp)}
            </div>
            <div className="web-vital-group">
              <h4 className="web-vital-label">Speed Index</h4>
              {renderWebVital('speedIndex', mobileMetrics.speedIndex)}
            </div>
            <div className="web-vital-group">
              <h4 className="web-vital-label">TBT</h4>
              {renderWebVital('tbt', mobileMetrics.tbt)}
            </div>
          </div>
        </div>
        <div className="performance-card">
          <h3 className="performance-card-title">Core Web Vitals - Desktop</h3>
          <div className="web-vitals-container">
            <div className="web-vital-group">
              <h4 className="web-vital-label">Performance Score</h4>
              {renderWebVital('performanceScore', desktopMetrics.performanceScore)}
            </div>
            <div className="web-vital-group">
              <h4 className="web-vital-label">LCP</h4>
              {renderWebVital('lcp', desktopMetrics.lcp)}
            </div>
            <div className="web-vital-group">
              <h4 className="web-vital-label">CLS</h4>
              {renderWebVital('cls', desktopMetrics.cls)}
            </div>
            <div className="web-vital-group">
              <h4 className="web-vital-label">FCP</h4>
              {renderWebVital('fcp', desktopMetrics.fcp)}
            </div>
            <div className="web-vital-group">
              <h4 className="web-vital-label">Speed Index</h4>
              {renderWebVital('speedIndex', desktopMetrics.speedIndex)}
            </div>
            <div className="web-vital-group">
              <h4 className="web-vital-label">TBT</h4>
              {renderWebVital('tbt', desktopMetrics.tbt)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceDetails; 